/**
 * Pure helpers behind the access matrix — draft building, dirty diffing and
 * resource-tree ordering. Kept free of React so the rules that decide what
 * "changed" and what "inherit resolves to" can be reasoned about (and tested)
 * on their own.
 */
import type { AccessPermission, AccessResource, GrantRow, GrantState } from '@/api/bi/access';

export const PERMISSIONS: AccessPermission[] = ['view', 'export', 'manage', 'schedule'];

/** Roles whose BI grants default to inherit (no explicit row) instead of deny. */
export const PRIVILEGED_BI_ROLES = new Set(['admin', 'manager']);

export type GrantDraft = Record<string, Partial<Record<AccessPermission, GrantState>>>;

export interface OrderedResource extends AccessResource {
  depth: number;
  /** Assignable descendants, used for the collapse affordance. */
  childSlugs: string[];
}

export function isPrivilegedRole(role: string | null | undefined): boolean {
  return role != null && PRIVILEGED_BI_ROLES.has(role);
}

export function defaultCellState(privileged: boolean): GrantState {
  return privileged ? 'inherit' : 'deny';
}

/**
 * Assignable resources in display order, annotated with tree depth and direct
 * children so rows can be indented and collapsed.
 */
export function buildResourceTree(resources: AccessResource[]): OrderedResource[] {
  const bySlug = new Map(resources.map((r) => [r.slug, r]));

  const depthFor = (resource: AccessResource): number => {
    let depth = 0;
    let parent = resource.parent ? bySlug.get(resource.parent) : undefined;
    // Guard against a cyclic parent chain in bad seed data.
    const seen = new Set<string>([resource.slug]);
    while (parent && !seen.has(parent.slug)) {
      seen.add(parent.slug);
      depth += 1;
      parent = parent.parent ? bySlug.get(parent.parent) : undefined;
    }
    return depth;
  };

  const assignable = resources
    .filter((r) => r.is_assignable)
    .map((r) => ({ ...r, depth: depthFor(r), childSlugs: [] as string[] }))
    .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));

  const byslugOrdered = new Map(assignable.map((r) => [r.slug, r]));
  for (const resource of assignable) {
    if (!resource.parent) continue;
    byslugOrdered.get(resource.parent)?.childSlugs.push(resource.slug);
  }
  return assignable;
}

/** Every assignable descendant of `slug`, transitively. */
export function descendantsOf(slug: string, ordered: OrderedResource[]): string[] {
  const bySlug = new Map(ordered.map((r) => [r.slug, r]));
  const out: string[] = [];
  const walk = (current: string) => {
    for (const child of bySlug.get(current)?.childSlugs ?? []) {
      out.push(child);
      walk(child);
    }
  };
  walk(slug);
  return out;
}

/** Server grants projected onto every assignable resource + permission. */
export function buildDraft(
  ordered: OrderedResource[],
  grants: GrantRow[] | undefined,
  cellDefault: GrantState,
): GrantDraft {
  const grantBySlug = new Map((grants ?? []).map((g) => [g.resource_slug, g.permissions]));
  const draft: GrantDraft = {};
  for (const resource of ordered) {
    const stored = grantBySlug.get(resource.slug) ?? {};
    const row: Partial<Record<AccessPermission, GrantState>> = {};
    for (const permission of PERMISSIONS) {
      row[permission] = (stored[permission] as GrantState | undefined) ?? cellDefault;
    }
    draft[resource.slug] = row;
  }
  return draft;
}

/** Slugs whose row differs from the loaded baseline. */
export function changedSlugs(draft: GrantDraft, baseline: GrantDraft): Set<string> {
  const out = new Set<string>();
  for (const slug of Object.keys(draft)) {
    const a = draft[slug] ?? {};
    const b = baseline[slug] ?? {};
    if (PERMISSIONS.some((p) => a[p] !== b[p])) out.add(slug);
  }
  return out;
}

/** Number of individual cells that differ — the count shown on the save bar. */
export function changedCellCount(draft: GrantDraft, baseline: GrantDraft): number {
  let count = 0;
  for (const slug of Object.keys(draft)) {
    const a = draft[slug] ?? {};
    const b = baseline[slug] ?? {};
    for (const permission of PERMISSIONS) {
      if (a[permission] !== b[permission]) count += 1;
    }
  }
  return count;
}

/** Row-level summary used by the whole-row allow/deny control. */
export function rowToggleState(
  permMap: Partial<Record<AccessPermission, GrantState>> | undefined,
  cellDefault: GrantState,
): 'allow' | 'deny' | 'mixed' {
  const resolved = PERMISSIONS.map((p) => permMap?.[p] ?? cellDefault);
  const first = resolved[0];
  if (first && resolved.every((v) => v === first)) {
    if (first === 'allow') return 'allow';
    if (first === 'deny') return 'deny';
  }
  return 'mixed';
}

export function draftToGrantRows(ordered: OrderedResource[], draft: GrantDraft, cellDefault: GrantState): GrantRow[] {
  return ordered.map((resource) => ({
    resource_slug: resource.slug,
    permissions: Object.fromEntries(
      PERMISSIONS.map((p) => [p, draft[resource.slug]?.[p] ?? cellDefault]),
    ) as Record<AccessPermission, GrantState>,
  }));
}

/**
 * What an `inherit` cell actually resolves to for a user.
 *
 * The backend checks the user override first, then the role grant, then falls
 * through to deny (`_direct_permission_for_resource` in feature/access.py). So
 * an inherited cell is decided entirely by the role — which the matrix used to
 * hide, leaving admins to guess what "inherit" meant for this person.
 */
export function resolveInherited(
  roleGrantBySlug: Map<string, Partial<Record<AccessPermission, GrantState>>>,
  slug: string,
  permission: AccessPermission,
): 'allow' | 'deny' {
  const state = roleGrantBySlug.get(slug)?.[permission];
  return state === 'allow' ? 'allow' : 'deny';
}
